import { Layout } from "../../UI/Layout/Layout.tsx";
import { NavBar } from "../../UI/Layout/NavBar.tsx";
import { Footer } from "../../UI/Layout/Footer.tsx";
import { FullPageLoading } from "../../UI/FullPageLoading.tsx";
import { useNavigate, useSearchParams } from "react-router";
import { AdminMainContainer } from "../../UI/Admin/AdminMainContainer.tsx";
import {
  CreateNormasTeachingCouponForm,
  CreateNormasTeachingCouponFormValues,
} from "./CreateNormasTeachingCouponForm.tsx";
import { useCreateNormasTeachingCoupon } from "../../ProductService/NormasTeachingCoupon/useCreateNormasTeachingCoupon.ts";
import { AdminAllNormasTeachingCouponScreenPath } from "./AdminAllNormasTeachingCouponScreen.tsx";
import { useAllNormasTeachingCoupons } from "../../ProductService/NormasTeachingCoupon/useAllNormasTeachingCoupons.ts";
import { CouponType } from "../../ProductService/NormasTeachingCoupon/useNormasTeachingCouponById.ts";
import { useAllNormasTeachingBundles } from "../../ProductService/NormasTeachingBundle/useAllNormasTeachingBundles.ts";
import { useAllNormasTeachingProducts } from "../../ProductService/NormasTeachingProduct/useAllNormasTeachingProducts.ts";
import { mapValidityToPayload } from "../../UI/Utils/validityDays.ts";
import { buildDuplicateCouponCode } from "./duplicateCouponCode.ts";

export const CreateNormasTeachingCouponScreenPath =
  "/admin/normasteaching/create-coupon";

// Seeds the create form from an existing coupon, see the table's "Duplica".
export const DuplicateCouponSearchParam = "duplicateFrom";

export function CreateNormasTeachingCouponScreen() {
  const { coupons, isLoading: isLoadingCoupons } =
    useAllNormasTeachingCoupons();
  const { products, isLoading: isLoadingProducts } =
    useAllNormasTeachingProducts();
  const { bundles, isLoading: isLoadingBundles } =
    useAllNormasTeachingBundles();

  const { createNormasTeachingCoupon, loading: creating } =
    useCreateNormasTeachingCoupon();

  const [searchParams] = useSearchParams();
  const duplicatedCouponId = searchParams.get(DuplicateCouponSearchParam);

  const navigate = useNavigate();

  const onSubmit = async (
    data: CreateNormasTeachingCouponFormValues,
  ): Promise<void> => {
    await createNormasTeachingCoupon({
      couponCode: data.couponCode,
      value: {
        amount:
          data.value.couponType === CouponType.Fixed
            ? data.value.amount * 100
            : data.value.amount,
        couponType: data.value.couponType,
      },
      maxNumberOfUses: data.maxNumberOfUses,
      ...mapValidityToPayload(data),
      applicableProducts: data.applicableProducts,
      isUnlimitedUses: data.isUnlimitedUses,
    });
    navigate(AdminAllNormasTeachingCouponScreenPath);
  };

  const isLoading = isLoadingCoupons || isLoadingProducts || isLoadingBundles;

  if (isLoading) return <FullPageLoading />;

  const duplicatedCoupon = duplicatedCouponId
    ? coupons.find((coupon) => coupon._id === duplicatedCouponId)
    : undefined;

  // Nothing is created until the admin submits: the source coupon only seeds
  // the form, with a free code suggested since coupon codes must be unique.
  const initialCoupon = duplicatedCoupon
    ? {
        ...duplicatedCoupon,
        couponCode: buildDuplicateCouponCode(
          duplicatedCoupon.couponCode,
          coupons,
        ),
      }
    : undefined;

  return (
    <Layout>
      <AdminMainContainer>
        <NavBar showAdminSideBarHandle />
        <div className={"p-4 flex-1 flex items-center"}>
          <div
            className={
              "max-w-[1024px]  w-full mx-auto bg-white p-4 rounded-lg flex flex-col gap-4"
            }
          >
            <h1 className={"font-bold text-2xl"}>
              {duplicatedCoupon ? "Duplica Coupon" : "Crea Coupon"}
            </h1>
            {duplicatedCoupon && (
              <p
                className={
                  "bg-blue-50 text-blue-800 text-sm rounded-lg p-3 border border-blue-200"
                }
              >
                Stai duplicando il coupon{" "}
                <span className={"font-medium"}>
                  {duplicatedCoupon.couponCode.toUpperCase()}
                </span>
                . I campi sono precompilati: modifica il codice sconto (deve
                essere univoco) e quello che ti serve, poi conferma per creare
                il nuovo coupon.
              </p>
            )}
            {duplicatedCouponId && !duplicatedCoupon && (
              <p
                className={
                  "bg-amber-50 text-amber-800 text-sm rounded-lg p-3 border border-amber-200"
                }
              >
                Coupon da duplicare non trovato: stai creando un nuovo coupon da
                zero.
              </p>
            )}
            <CreateNormasTeachingCouponForm
              key={duplicatedCoupon?._id ?? "new-coupon"}
              onSubmit={onSubmit}
              coupons={coupons}
              creating={creating}
              products={products}
              bundles={bundles}
              initialCoupon={initialCoupon}
            />
          </div>
        </div>
      </AdminMainContainer>
      <Footer />
    </Layout>
  );
}
